import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { themeQuery } from '#admin/features/theme/queries.ts'
import { ThemeForm } from '#admin/features/theme/update-theme/form.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { ThemeSkeleton } from '#admin/features/theme/theme-skeleton.tsx'

/**
 * `/settings/theme`.
 *
 * The route owns the URL, the heading and the loader. **The theme feature owns everything
 * else**: the contract, the validation, the preview and the publish mutation. That split is
 * the router guide's worked example for this exact page, and it is what lets the same editor
 * be dropped into another console later without dragging a route with it.
 */
export const Route = createFileRoute('/_authenticated/settings/theme')({
  pendingComponent: ThemeSkeleton,
  staticData: { crumb: { parents: [{ label: 'Settings' }], label: 'Theme' } },
  loader: ({ context }) => context.queryClient.ensureQueryData(themeQuery()),
  component: ThemeSettingsPage,
})

function ThemeSettingsPage() {
  const { data } = useSuspenseQuery(themeQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'identity.manage')

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-semibold">Theme</h1>
        <p className="text-sm text-muted-foreground">
          Choose your edition&rsquo;s colors, font and corner style. Changes show in the preview
          straight away; publishing applies them everywhere your delegates and staff see.
        </p>
      </header>

      {canManage ? null : <ReadOnlyNotice area="the theme" />}
      <ThemeForm initial={data.theme} readOnly={!canManage} />
    </div>
  )
}
